import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Checkbox, Stack, TextField, Typography } from "@mui/material";
import { adminFetch } from "../services/adminFetch";
import { Colors } from "../utils/colors";
import { formatAdminDateTime } from "../utils/adminDateTime";

const panel = { background: "linear-gradient(145deg,#0b4269,#072d4a)", color: "#fff",
  border: "1px solid rgba(251,192,45,.2)", borderRadius: 3, p: { xs: 2, md: 3 }, minWidth: 0,
  maxWidth: "100%", overflow: "hidden" };

export default function InfluencerTermsManager() {
  const [terms, setTerms] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    const response = await adminFetch("/api/admin/influencer-terms");
    if (!response?.status) throw new Error(response?.message || "Unable to load Influencer Terms");
    setTerms(response.terms); setTitle(response.terms.title); setContent(response.terms.content);
  };
  useEffect(() => { load().catch((error) => setNotice({ type: "error", text: error.message })); }, []);

  const update = async () => {
    setBusy(true); setNotice(null);
    try {
      const response = await adminFetch("/api/admin/influencer-terms", {
        method: "PUT", body: JSON.stringify({ title, content, password, confirm: confirmed })
      });
      if (!response?.status) throw new Error(response?.message || "Unable to update Influencer Terms");
      setTerms(response.terms); setEditing(false); setPreview(false); setConfirmOpen(false);
      setPassword(""); setConfirmed(false);
      setNotice({ type: "success", text: "Influencer Terms updated. New invitations now show this document." });
    } catch (error) { setNotice({ type: "error", text: error.message }); }
    finally { setBusy(false); }
  };

  return <Stack spacing={2.5}>
    {notice && <Alert severity={notice.type} onClose={() => setNotice(null)}>{notice.text}</Alert>}
    <Card sx={panel}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0 }}><Typography variant="h5" sx={{ color: Colors.accent, fontWeight: 900 }}>Influencer Terms &amp; Conditions</Typography>
          <Typography sx={{ color: "#b8cad7" }}>One authoritative document used for all new Influencer invitations.</Typography>
          {terms?.updated_at && <Typography variant="caption" sx={{ color: "#91aabd" }}>Last updated {formatAdminDateTime(terms.updated_at)}</Typography>}</Box>
        {!editing && <Button variant="contained" onClick={() => setEditing(true)} sx={{ bgcolor: Colors.accent, color: "#08243d", alignSelf: "flex-start" }}>Edit</Button>}
      </Stack>
    </Card>
    <Card sx={panel}>
      {editing ? <Stack spacing={2}>
        <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} fullWidth />
        <TextField label="Terms content" value={content} onChange={(event) => setContent(event.target.value)} multiline minRows={18} fullWidth sx={{ "& textarea": { overflowWrap: "anywhere" } }} />
        <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
          <Button variant="outlined" onClick={() => setPreview((value) => !value)}>{preview ? "Hide Preview" : "Preview"}</Button>
          <Button variant="contained" disabled={busy} onClick={() => setConfirmOpen(true)}>Save and Publish</Button>
          <Button onClick={() => { setTitle(terms.title); setContent(terms.content); setEditing(false); setPreview(false); }}>Cancel</Button>
        </Stack>
      </Stack> : <><Typography variant="overline" sx={{ color: "#91aabd" }}>Current Terms</Typography>
        <Typography variant="h6" sx={{ color: Colors.accent, mb: 1 }}>{terms?.title}</Typography>
        {terms?.updated_by_admin_id && <Typography variant="caption" sx={{ color: "#91aabd", display: "block", mb: 2 }}>Updated by Admin #{terms.updated_by_admin_id}</Typography>}
        <Typography component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit", m: 0 }}>{terms?.content}</Typography></>}
    </Card>
    {editing && preview && <Card sx={{ ...panel, bgcolor: "#f7f4e9", color: "#183449", background: "#f7f4e9" }}>
      <Typography variant="h6" mb={2}>{title}</Typography><Typography component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit", m: 0 }}>{content}</Typography>
    </Card>}
    <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Authorize Terms Update</DialogTitle><DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="warning">This immediately replaces the current terms shown to new Influencers. Existing Influencers will not be asked to re-accept.</Alert>
        <TextField label="Super Admin password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} fullWidth />
        <FormControlLabel control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />} label="I confirm that I want to update the current Influencer Terms." />
      </Stack></DialogContent><DialogActions><Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={update} disabled={busy || !password || !confirmed}>Confirm Update</Button></DialogActions>
    </Dialog>
  </Stack>;
}
